import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import type { Text } from "../i18n";

/** One line of a signing request: the plain words (D45), the identifier line, and what it does with the wallet's key. */
export interface SignLine {
  plain?: Text;
  text: Text;
  address?: string;
  /** An ETH estimate for the fee line; undefined = a message signature, no gas. */
  gas?: number;
  calls?: string[];
  /**
   * Runs the line with the wallet's key: the transaction it sent, and lines to add after this one when the line
   * learned about them (a sign-in whose settings record points at another memory service).
   */
  run: () => Promise<{ tx?: string; more?: SignLine[] } | void>;
}

export interface LineState {
  state: "pending" | "signing" | "signed" | "failed";
  tx?: string;
  error?: string;
}

export type SignResult = "signed" | "rejected" | "failed";

export interface SignRequest {
  title: Text;
  amount?: number; // ≈ wp
  tokens?: Array<{ amount: number; symbol: string }>;
  note?: Text;
  lines: SignLine[];
  onLine?: (index: number, state: LineState) => void;
  onDone: (result: SignResult, lines: LineState[]) => void;
}

interface Queued extends SignRequest {
  id: string;
}

export type WalletTab = "balances" | "authorizations";

interface WalletContextValue {
  queue: Queued[];
  lines: LineState[];
  phase: "ask" | "signing" | "closing";
  request: (req: SignRequest) => void;
  sign: () => Promise<void>;
  reject: () => void;
  popoverOpen: boolean;
  openPopover: () => void;
  closePopover: () => void;
  panelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  tab: WalletTab;
  setTab: (t: WalletTab) => void;
}

const WalletContext = createContext<WalletContextValue | null>(null);
const CLOSE_MS = 250;
const wait = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));
const freshLines = (req?: SignRequest): LineState[] => (req ? req.lines.map(() => ({ state: "pending" })) : []);

/** The signing panel's queue (D32): one request at a time, its lines run in sequence with the key kept in the browser. */
export function WalletProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Queued[]>([]);
  const [lines, setLines] = useState<LineState[]>([]);
  const [phase, setPhase] = useState<"ask" | "signing" | "closing">("ask");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [tab, setTab] = useState<WalletTab>("balances");
  const queueRef = useRef<Queued[]>([]);
  const linesRef = useRef<LineState[]>([]);
  const busy = useRef(false);
  const counter = useRef(0);

  const commit = useCallback((q: Queued[]) => {
    queueRef.current = q;
    setQueue(q);
  }, []);

  const request = useCallback(
    (req: SignRequest) => {
      const first = queueRef.current.length === 0;
      commit([...queueRef.current, { ...req, id: `w${++counter.current}` }]);
      setPopoverOpen(false);
      if (first) {
        linesRef.current = freshLines(req);
        setLines(linesRef.current);
        setPhase("ask");
      }
    },
    [commit],
  );

  const finish = useCallback(
    async (result: SignResult) => {
      const current = queueRef.current[0];
      setPhase("closing");
      await wait(CLOSE_MS);
      const rest = queueRef.current.slice(1);
      commit(rest);
      const done = linesRef.current;
      linesRef.current = freshLines(rest[0]);
      setLines(linesRef.current);
      setPhase("ask");
      busy.current = false;
      current?.onDone(result, done);
    },
    [commit],
  );

  const sign = useCallback(async () => {
    const req = queueRef.current[0];
    if (!req || busy.current) return;
    busy.current = true;
    setPhase("signing");
    const update = (i: number, s: LineState) => {
      linesRef.current = linesRef.current.map((l, idx) => (idx === i ? s : l));
      setLines(linesRef.current);
      req.onLine?.(i, s);
    };
    for (let i = 0; i < req.lines.length; i++) {
      update(i, { state: "signing" });
      try {
        const r = await req.lines[i]!.run();
        if (r && r.more && r.more.length > 0) {
          // the lines this one learned about follow it in the same request
          req.lines.splice(i + 1, 0, ...r.more);
          linesRef.current = [
            ...linesRef.current.slice(0, i + 1),
            ...r.more.map((): LineState => ({ state: "pending" })),
            ...linesRef.current.slice(i + 1),
          ];
          setLines(linesRef.current);
          commit([...queueRef.current]);
        }
        update(i, { state: "signed", tx: r && "tx" in r ? r.tx : undefined });
      } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        update(i, { state: "failed", error });
        toast.error(error);
        await wait(600);
        await finish("failed");
        return;
      }
    }
    await wait(300);
    await finish("signed");
  }, [finish, commit]);

  const reject = useCallback(() => {
    if (!queueRef.current[0] || busy.current) return;
    busy.current = true;
    void finish("rejected");
  }, [finish]);

  const value = useMemo<WalletContextValue>(
    () => ({
      queue,
      lines,
      phase,
      request,
      sign,
      reject,
      popoverOpen,
      openPopover: () => setPopoverOpen(true),
      closePopover: () => setPopoverOpen(false),
      panelOpen,
      openPanel: () => {
        setPopoverOpen(false);
        setPanelOpen(true);
      },
      closePanel: () => setPanelOpen(false),
      tab,
      setTab,
    }),
    [queue, lines, phase, request, sign, reject, popoverOpen, panelOpen, tab],
  );
  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet(): WalletContextValue {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error("useWallet needs WalletProvider");
  return ctx;
}
